import { UserGeneralProfile } from "@jugl-web/rest-api";
import { assert, getUniqueId, useMemoCompare } from "@jugl-web/utils";
import isEqual from "lodash/isEqual";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUserProfileProviderContext } from "../providers/UserProfileProvider";

interface UseMultipleUserProfilesOptions {
  entityId: string | undefined;
  userIds: string[];
  skip?: boolean;
}

export const useMultipleUserProfiles = ({
  entityId,
  userIds,
  skip,
}: UseMultipleUserProfilesOptions) => {
  const { requestUserProfile, getCachedUserProfile } =
    useUserProfileProviderContext();

  const [profiles, setProfiles] = useState<UserGeneralProfile[]>(() => {
    if (!entityId) {
      return [];
    }

    return userIds.reduce<UserGeneralProfile[]>((acc, userId) => {
      const profile = getCachedUserProfile(entityId, userId);

      if (profile) {
        acc.push(profile);
      }

      return acc;
    }, []);
  });

  const [isLoading, setIsLoading] = useState(false);

  const batchRequestIdRef = useRef(getUniqueId());

  const memoizedUserIds = useMemoCompare(userIds, isEqual);

  const profileByUserId = useMemo<Record<string, UserGeneralProfile>>(() => {
    const result: Record<string, UserGeneralProfile> = {};

    profiles.forEach((profile) => {
      result[profile.id] = profile;
    });

    return result;
  }, [profiles]);

  const loadProfiles = useCallback(async () => {
    assert(!!entityId, "entityId is required");

    setIsLoading(true);

    const batchRequestId = batchRequestIdRef.current;

    const results = await Promise.allSettled(
      memoizedUserIds.map((userId) => requestUserProfile({ entityId, userId }))
    );

    if (batchRequestId !== batchRequestIdRef.current) {
      return;
    }

    setProfiles(
      results.reduce<UserGeneralProfile[]>(
        (acc, result) =>
          result.status === "fulfilled" ? [...acc, result.value.profile] : acc,
        []
      )
    );

    setIsLoading(false);
  }, [entityId, memoizedUserIds, requestUserProfile]);

  useEffect(() => {
    if (!entityId || skip) {
      return undefined;
    }

    loadProfiles();

    return () => {
      batchRequestIdRef.current = getUniqueId();
    };
  }, [entityId, loadProfiles, skip]);

  return { profiles, profileByUserId, isLoading };
};
